<script setup lang="ts">
// Private search over messages, library and repository names, and the files in every
// library this device can open, all on this device: the server never sees the query.
import { computed, onMounted, ref, shallowRef } from 'vue'
import { useRouter } from 'vue-router'
import type { OpenLibrary } from '../sync/engine'
import { annotations } from '../lib/annotations'
import { mayModerate } from '../lib/roles'
import { displayName, formatTime } from '../lib/format'
import { loadLibraryIndex, searchIndex, searchLibraryNames, type FileHit, type LibraryIndex } from '../lib/librarySearch'
import { entryRef, playableBlob } from '../lib/libraryPlay'
import { search } from '../lib/search'
import { usePlayer } from '../stores/player'
import { useSession } from '../stores/session'
import Modal from './Modal.vue'

const emit = defineEmits<{ close: [] }>()
const session = useSession()
const player = usePlayer()
const router = useRouter()
const q = ref('')

// Deleted and hidden messages don't turn up (hides count only from those allowed to).
const edits = (conv: string) => {
  const c = session.conversations.get(conv)
  const space = c?.space_id ? session.spaces.find((s) => s.id === c.space_id) : undefined
  const state = annotations(session.messages[conv] ?? [], (actor, author) => !!space && mayModerate(space, actor, author, 'hide_messages'))
  for (const s of state.values()) if (s.hidden) s.deleted = true
  return state
}
const hits = computed(() => search(session.messages, q.value, edits))

// Libraries and their files, loaded (and decrypted) once when search opens.
const index = shallowRef<LibraryIndex<OpenLibrary>>({ libraries: [], entries: new Map() })
const indexing = ref(0)
const FILE_LIMIT = 50

onMounted(() =>
  loadLibraryIndex(session, (i, pending) => {
    index.value = { ...i }
    indexing.value = pending
  }).catch(() => undefined),
)

const libraries = computed(() => index.value.libraries)
const libraryHits = computed(() => searchLibraryNames(libraries.value, q.value))
const fileHits = computed(() => searchIndex(index.value, q.value))
const libraryName = (id: string) => libraries.value.find((l) => l.id === id)?.name ?? ''
const searching = computed(() => q.value.trim().length >= 2)

function where(conv: string): string {
  const c = session.conversations.get(conv)
  if (!c) return ''
  if (c.kind === 'channel') {
    const space = session.spaces.find((s) => s.id === c.space_id)
    return `${space?.name ?? ''} › #${c.name}`
  }
  return `@ ${c.members.filter((m) => m !== session.me).map((m) => displayName(session.profiles, m)).join(', ')}`
}

async function open(conv: string, id: string): Promise<void> {
  const c = session.conversations.get(conv)
  emit('close')
  if (c?.kind === 'channel' && c.space_id) {
    await router.push({ name: 'space', params: { spaceId: c.space_id, convId: conv }, query: { m: id } })
  } else {
    await router.push({ name: 'dm', params: { convId: conv }, query: { m: id } })
  }
}

/** Opens a library, filtered to a file if one was picked. */
async function openLibrary(library: string, file?: string): Promise<void> {
  emit('close')
  await router.push({ name: 'library', query: { lib: library, ...(file ? { q: file } : {}) } })
}

const playable = (h: FileHit) => (h.kind === 'audio' || h.kind === 'video') && !!h.entry && !!playableBlob(h.entry)
function play(h: FileHit): void {
  if (!h.entry) return
  player.play(entryRef(h.library, h.entry))
  emit('close')
}

const kindMark = (k: FileHit['kind']) => ({ audio: 'AUD', video: 'VID', image: 'IMG', file: 'DOC' })[k]
</script>

<template>
  <Modal title="Search" @close="emit('close')">
    <input v-model="q" class="input" placeholder="Search messages, libraries and files" autofocus data-testid="search-input" />
    <p class="mono mt-2 text-text-muted">
      LOCAL INDEX · searched on this device, never on the server.
      <template v-if="indexing > 0"> Reading {{ indexing }} librar{{ indexing === 1 ? 'y' : 'ies' }}…</template>
    </p>
    <div class="mt-3 max-h-[60vh] overflow-y-auto">
      <template v-if="libraryHits.length">
        <h3 class="label mt-2">Libraries · {{ libraryHits.length }}</h3>
        <ul>
          <li v-for="l in libraryHits" :key="l.id">
            <button class="flex w-full items-center gap-3 border-b border-border px-2 py-2.5 text-left hover:bg-bg-hover" data-testid="search-library" @click="openLibrary(l.id)">
              <span class="mono w-8 shrink-0 text-text-muted">{{ l.kind === 'git' ? 'GIT' : 'LIB' }}</span>
              <span class="truncate text-sm">{{ l.name }}</span>
              <span class="mono ml-auto shrink-0 text-text-muted">{{ l.kind === 'git' ? 'repository' : `${l.items} files` }}</span>
            </button>
          </li>
        </ul>
      </template>

      <template v-if="fileHits.length">
        <h3 class="label mt-4">Files · {{ fileHits.length > FILE_LIMIT ? `${FILE_LIMIT}+` : fileHits.length }}</h3>
        <ul>
          <li v-for="h in fileHits.slice(0, FILE_LIMIT)" :key="h.library + h.id" class="flex items-center border-b border-border hover:bg-bg-hover">
            <button class="flex min-w-0 flex-1 items-center gap-3 px-2 py-2.5 text-left" data-testid="search-file" @click="openLibrary(h.library, h.title)">
              <span class="mono w-8 shrink-0 text-text-muted">{{ kindMark(h.kind) }}</span>
              <span class="min-w-0 flex-1">
                <span class="block truncate text-sm">{{ h.title }}</span>
                <span class="mono block truncate text-text-muted">{{ libraryName(h.library) }}<template v-if="h.detail"> · {{ h.detail }}</template></span>
              </span>
            </button>
            <button v-if="playable(h)" class="mono shrink-0 px-3 text-text-muted uppercase hover:text-text-primary" data-testid="search-file-play" @click="play(h)">Play</button>
          </li>
        </ul>
      </template>

      <template v-if="hits.length">
        <h3 class="label mt-4">Messages · {{ hits.length }}</h3>
        <ul>
          <li v-for="h in hits" :key="h.msg.conv + h.msg.seq">
            <button class="w-full border-b border-border px-2 py-2.5 text-left hover:bg-bg-hover" data-testid="search-hit" @click="open(h.msg.conv, h.msg.envelope.id)">
              <div class="mono flex items-baseline gap-2 text-text-muted">
                <span class="text-text-secondary">{{ displayName(session.profiles, h.msg.sender.user_id) }}</span>
                <span class="truncate">{{ where(h.msg.conv) }}</span>
                <span class="ml-auto shrink-0">{{ formatTime(h.msg.envelope.ts || h.msg.receivedAt) }}</span>
              </div>
              <div class="truncate text-sm">{{ h.snippet }}</div>
            </button>
          </li>
        </ul>
      </template>

      <p v-if="searching && !hits.length && !fileHits.length && !libraryHits.length && indexing === 0" class="px-2 py-3 text-sm text-text-muted">No matches.</p>
    </div>
  </Modal>
</template>
